// ==========================================================================
// Axios Instance - Configured HTTP client with auth interceptors
// ==========================================================================

import axios, { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { showToast } from '@/stores/toastStore';

// Error response shape from backend
interface ApiErrorResponse {
  message?: string;
  errors?: Record<string, string[]>;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem('access_token');

    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    // Handle 401 Unauthorized - clear token and let auth gate redirect
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      return Promise.reject(error);
    }

    // Extract error message and show toast for non-401 errors
    const data = error.response?.data;
    let errorMessage = 'Something went wrong. Please try again.';

    if (data?.message) {
      errorMessage = data.message;
    } else if (data?.errors) {
      // Flatten validation errors
      const messages = Object.values(data.errors).flat();
      if (messages.length > 0) {
        errorMessage = messages[0];
      }
    } else if (error.message === 'Network Error') {
      errorMessage = 'Network error. Please check your connection.';
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'Request timed out. Please try again.';
    }

    showToast(errorMessage, 'error');

    // Report all errors to Sentry
    Sentry.captureException(error, {
      tags: {
        api_url: error.config?.url,
        status_code: error.response?.status?.toString(),
      },
    });

    return Promise.reject(error);
  }
);

export default api;
